// Reading ui-router state `data` the way ui-router reads it: INHERITED.
//
// ⚠️ PER KEY, NOT PER ROUTE, and the difference is not academic. `useRouteChrome` took the
// longest route matching the path and read `bodyClass` off it — which is right whenever the
// leaf declares one and silently wrong whenever an ancestor does. `/extension/shortlist`
// declares `bodyClass: 'no-min-width'` and `/extension/shortlist/review` declares
// `{ viewWidth, tabGroup, tabName }` and no bodyClass, so the leaf won and the class was never
// applied: the extension chrome rendered 1190px wide inside a 378px frame, with the header's
// pin and actions icons sitting at x=1166, off the edge of the screen.
//
// Upstream has no such bug because ui-router merges `data` down the state chain. This does the
// same thing against the flat route table: for each key, the nearest ancestor that declares it
// wins. The auth screens were unaffected only because each of them declares its own bodyClass.
import { routes } from '@woo-ext/protocol'

/**
 * The value of `key` for `pathname`, taken from the nearest route that declares it.
 *
 * Matching is by path prefix, which is how the ported route table represents the state chain:
 * `/a/b/c` inherits from `/a/b` and `/a`, longest first.
 */
export function inheritedData(pathname: string, key: string): unknown {
  return routes
    .filter((route) => pathname === route.path || pathname.startsWith(`${route.path}/`))
    .filter((route) => route.data[key] !== undefined)
    .sort((a, b) => b.path.length - a.path.length)[0]?.data[key]
}

/** `inheritedData` narrowed to a string, for the several keys that are one. */
export function inheritedString(pathname: string, key: string): string | undefined {
  const value = inheritedData(pathname, key)
  return typeof value === 'string' ? value : undefined
}
